// Login.js - Dark WebGL Dot Matrix Sign In / Sign Up Page
import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../hooks/useAuth';
import { authAPI, utils } from '../api/authAPI';
import SeoMeta from '../../../components/SeoMeta/SeoMeta';
import './Login.css';
import GlobalFooter from '../../../components/Footer/GlobalFooter';
import {
  readPendingCheckout,
  runPendingCheckout,
  storePendingCheckoutFromUrl,
} from '../../checkout/pendingCheckout';
import { applyHandoffContinuation, getPendingHandoffToken } from '../../edu-landing/handoffContinuation';
import {
  clearPasswordChangeSignIn,
  recordPasswordChangeSignIn,
} from '../passwordChangeSignIn';
import { isPathbuddyHost } from '../../../config/eduHost';
import { EDU_CANONICAL_BASE, withAppBasename } from '../../../config/appBase';
import { oauthRedirectUri } from '../oauthRedirect';

const GoogleIcon = () => (
  <svg viewBox="0 0 24 24" style={{ width: 16, height: 16, flexShrink: 0 }}>
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
  </svg>
);

const LinkedInIcon = () => (
  <svg viewBox="0 0 24 24" fill="#0A66C2" style={{ width: 16, height: 16, flexShrink: 0 }}>
    <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"/>
  </svg>
);

const Login = ({ onLoginSuccess, locationState }) => {
  const canvasRef = useRef(null);
  const { login, register } = useAuth();
  const [isSignUp, setIsSignUp] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [loginWithPassword, setLoginWithPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: ''
  });
  const [agreeTerms, setAgreeTerms] = useState(false);
  const [errors, setErrors] = useState({});

  // Canvas WebGL Dot Matrix Shader Effect
  useEffect(() => {
    let active = true;
    let renderer;
    let geometry;
    let material;
    let scene;
    let camera;
    let animationId;

    const initThree = (THREE) => {
      if (!canvasRef.current || !active) return;
      const canvas = canvasRef.current;
      renderer = new THREE.WebGLRenderer({ canvas, alpha: true, antialias: false });
      renderer.setPixelRatio(window.devicePixelRatio);
      renderer.setSize(window.innerWidth, window.innerHeight);

      scene = new THREE.Scene();
      camera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);

      const uniforms = {
        u_time: { value: 0 },
        u_resolution: { value: new THREE.Vector2(window.innerWidth * 2, window.innerHeight * 2) },
        u_opacities: { value: [0.3, 0.3, 0.3, 0.5, 0.5, 0.5, 0.8, 0.8, 0.8, 1.0] },
        u_colors: { value: [
          new THREE.Vector3(1, 1, 1),
          new THREE.Vector3(1, 1, 1),
          new THREE.Vector3(1, 1, 1),
          new THREE.Vector3(1, 1, 1),
          new THREE.Vector3(1, 1, 1),
          new THREE.Vector3(1, 1, 1)
        ] },
        u_total_size: { value: 20.0 },
        u_dot_size: { value: 6.0 },
        u_reverse: { value: 0 }
      };

      material = new THREE.ShaderMaterial({
        vertexShader: `
          precision mediump float;
          uniform vec2 u_resolution;
          out vec2 fragCoord;
          void main() {
            gl_Position = vec4(position, 1.0);
            fragCoord = (position.xy + 1.0) * 0.5 * u_resolution;
            fragCoord.y = u_resolution.y - fragCoord.y;
          }
        `,
        fragmentShader: `
          precision mediump float;
          in vec2 fragCoord;

          uniform float u_time;
          uniform float u_opacities[10];
          uniform vec3 u_colors[6];
          uniform float u_total_size;
          uniform float u_dot_size;
          uniform vec2 u_resolution;
          uniform int u_reverse;

          out vec4 fragColor;

          float PHI = 1.61803398874989484820459;
          float random(vec2 xy) {
              return fract(tan(distance(xy * PHI, xy) * 0.5) * xy.x);
          }

          void main() {
              vec2 st = fragCoord.xy;
              st.x -= abs(floor((mod(u_resolution.x, u_total_size) - u_dot_size) * 0.5));
              st.y -= abs(floor((mod(u_resolution.y, u_total_size) - u_dot_size) * 0.5));

              float opacity = step(0.0, st.x) * step(0.0, st.y);

              vec2 st2 = vec2(int(st.x / u_total_size), int(st.y / u_total_size));

              float frequency = 5.0;
              float show_offset = random(st2);
              float rand = random(st2 * floor((u_time / frequency) + show_offset + frequency));
              opacity *= u_opacities[int(rand * 10.0)];
              opacity *= 1.0 - step(u_dot_size / u_total_size, fract(st.x / u_total_size));
              opacity *= 1.0 - step(u_dot_size / u_total_size, fract(st.y / u_total_size));

              vec3 color = u_colors[int(show_offset * 6.0)];

              float animation_speed_factor = 3.0;
              vec2 center_grid = u_resolution / 2.0 / u_total_size;
              float dist_from_center = distance(center_grid, st2);

              float timing_offset_intro = dist_from_center * 0.01 + (random(st2) * 0.15);

              float current_timing_offset = timing_offset_intro;
              opacity *= step(current_timing_offset, u_time * animation_speed_factor);
              opacity *= clamp((1.0 - step(current_timing_offset + 0.1, u_time * animation_speed_factor)) * 1.25, 1.0, 1.25);

              fragColor = vec4(color, opacity);
              fragColor.rgb *= fragColor.a;
          }
        `,
        uniforms: uniforms,
        glslVersion: THREE.GLSL3,
        blending: THREE.CustomBlending,
        blendSrc: THREE.SrcAlphaFactor,
        blendDst: THREE.OneFactor,
        transparent: true
      });

      geometry = new THREE.PlaneGeometry(2, 2);
      const mesh = new THREE.Mesh(geometry, material);
      scene.add(mesh);

      const startTime = performance.now();
      const animate = () => {
        if (!active) return;
        animationId = requestAnimationFrame(animate);
        uniforms.u_time.value = (performance.now() - startTime) / 1000.0;
        renderer.render(scene, camera);
      };
      animate();

      const handleResize = () => {
        if (!renderer || !active) return;
        renderer.setSize(window.innerWidth, window.innerHeight);
        uniforms.u_resolution.value.set(window.innerWidth * 2, window.innerHeight * 2);
      };
      window.addEventListener('resize', handleResize);

      return () => {
        window.removeEventListener('resize', handleResize);
      };
    };

    if (window.THREE) {
      const cleanUp = initThree(window.THREE);
      return () => {
        active = false;
        if (cleanUp) cleanUp();
        if (animationId) cancelAnimationFrame(animationId);
        if (renderer) renderer.dispose();
        if (geometry) geometry.dispose();
        if (material) material.dispose();
      };
    } else {
      const existingScript = document.querySelector('script[src*="three.min.js"]');
      if (existingScript) {
        existingScript.addEventListener('load', () => {
          if (window.THREE && active) {
            initThree(window.THREE);
          }
        });
      } else {
        const script = document.createElement('script');
        script.src = "https://cdnjs.cloudflare.com/ajax/libs/three.js/r128/three.min.js";
        script.async = true;
        script.onload = () => {
          if (window.THREE && active) {
            initThree(window.THREE);
          }
        };
        document.head.appendChild(script);
      }
    }

    return () => {
      active = false;
      if (animationId) cancelAnimationFrame(animationId);
      if (renderer) renderer.dispose();
      if (geometry) geometry.dispose();
      if (material) material.dispose();
    };
  }, []);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const signupParam = urlParams.get('signup');
    const emailParam = urlParams.get('email');
    
    if (signupParam === 'true') {
      setIsSignUp(true);
    }
    if (emailParam) {
      setFormData(prev => ({ ...prev, email: emailParam }));
    }
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
    if (errors.submit) setErrors(prev => ({ ...prev, submit: '' }));
  };

  const validateForm = () => {
    const newErrors = {};
    if (isSignUp && !formData.name.trim()) {
      newErrors.name = 'Full name is required';
    }
    if (!formData.email.trim()) {
      newErrors.email = 'E-mail address is required';
    } else if (!utils.isValidEmail(formData.email.trim())) {
      newErrors.email = 'Please enter a valid e-mail address';
    }
    if (isSignUp && !formData.password) {
      newErrors.password = 'Password is required';
    } else if (isSignUp && formData.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    } else if (!isSignUp && loginWithPassword && !formData.password) {
      newErrors.password = 'Password is required';
    }
    if (isSignUp && !agreeTerms) {
      newErrors.agreeTerms = 'You must agree to Terms & Privacy Policy';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handlePostAuthRedirect = async (userData) => {
    if (userData?.must_change_password) {
      if (onLoginSuccess) {
        onLoginSuccess(userData);
      }
      return;
    }
    const pending = readPendingCheckout();
    const tierName = pending.tier;
    const examType = pending.exam;

    let outcome = { action: 'none' };
    try {
      outcome = await runPendingCheckout(userData);
    } catch (err) {
      console.error('Checkout redirect error:', err);
    }
    if (outcome.action === 'redirect') {
      window.location.href = outcome.url;
      return;
    }
    if (outcome.action === 'practice') {
      window.location.href = withAppBasename(`/practice/${outcome.exam}`);
      return;
    }
    if (outcome.action === 'pricing') {
      window.location.href = withAppBasename('/pricing');
      return;
    }
    if (outcome.action === 'no_url') {
      // No checkout URL: no plan is written client-side (the server never
      // read it); the practice page shows whatever the server granted.
      window.location.href = withAppBasename(examType ? `/practice/${examType}` : '/pricing');
      return;
    }

    if (onLoginSuccess) {
      onLoginSuccess(userData);
    } else if (tierName && (tierName.toLowerCase().includes('beginning') || tierName.toLowerCase().includes('free'))) {
      window.location.href = withAppBasename(examType ? `/practice/${examType}` : '/pricing');
    } else {
      window.location.href = withAppBasename('/chat');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setIsLoading(true);
    setErrors({});

    const searchParams = new URLSearchParams(window.location.search);
    const tierParam = searchParams.get('tier') || sessionStorage.getItem('pending_checkout_tier') || 'free';
    const examParam = searchParams.get('exam') || sessionStorage.getItem('pending_checkout_exam') || 'undecided';

    const pendingHandoffToken = getPendingHandoffToken();
    let recordedPasswordChange = false;

    try {
      if (isSignUp) {
        const result = await register(
          formData.name.trim(),
          formData.email.trim(),
          formData.password,
          {
            plan: tierParam,
            primary_subject: examParam
          },
          pendingHandoffToken
        );
        if (result.success) {
          applyHandoffContinuation(result);
          setErrors({ success: 'Account created! Preparing your dashboard...' });
          handlePostAuthRedirect(result.user);
        } else {
          setErrors({ submit: result.error || 'Failed to create account.' });
        }
      } else {
        if (loginWithPassword) {
          recordPasswordChangeSignIn({ source: 'password', locationState });
          recordedPasswordChange = true;
          const result = await login(formData.email.trim(), formData.password, true, pendingHandoffToken);
          if (result.success) {
            applyHandoffContinuation(result);
            if (!result.user?.must_change_password) {
              clearPasswordChangeSignIn();
            }
            handlePostAuthRedirect(result.user);
          } else {
            clearPasswordChangeSignIn();
            setErrors({ submit: result.error || 'Incorrect email or password. Please try again.' });
          }
        } else {
          // authAPI.sendMagicLink resolves with the parsed JSON body
          // directly (APIService.makeRequest has no axios-style `.data`
          // envelope), and only ever resolves on a 200 — the backend
          // returns { message: '...' } on success and throws (rejects,
          // caught by the outer catch below) on any error response. So a
          // resolved promise here always means the link was sent.
          await authAPI.sendMagicLink(formData.email.trim(), pendingHandoffToken);
          setErrors({ success: 'Magic link sent! Check your email inbox. No email? Check the address, or sign up if you are new.' });
        }
      }
    } catch (err) {
      if (recordedPasswordChange) {
        clearPasswordChangeSignIn();
      }
      setErrors({ submit: err.message || 'An unexpected error occurred.' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleSSO = () => {
    storePendingCheckoutFromUrl();

    const client_id = process.env.REACT_APP_GOOGLE_CLIENT_ID || '441415384283-nckqhtu1pg8fe8reevmp15l0ppsj545u.apps.googleusercontent.com';
    const state = Math.random().toString(36).substring(2, 15);
    sessionStorage.setItem('google_oauth_state', state);
    const redirect_uri = oauthRedirectUri('google');
    const scope = 'openid profile email';
    const authorizationUrl = `https://accounts.google.com/o/oauth2/v2/auth?` +
      `response_type=code` +
      `&client_id=${client_id}` +
      `&redirect_uri=${encodeURIComponent(redirect_uri)}` +
      `&scope=${encodeURIComponent(scope)}` +
      `&state=${state}` +
      `&prompt=select_account`;
    window.location.href = authorizationUrl;
  };

  const handleLinkedInSSO = () => {
    storePendingCheckoutFromUrl();

    const client_id = process.env.REACT_APP_LINKEDIN_CLIENT_ID || '785tfmn0yipi5w';
    const state = Math.random().toString(36).substring(2, 15);
    sessionStorage.setItem('linkedin_oauth_state', state);
    const redirect_uri = oauthRedirectUri('linkedin');
    const scope = 'openid profile email';
    const authorizationUrl = `https://www.linkedin.com/oauth/v2/authorization?` +
      `response_type=code` +
      `&client_id=${client_id}` +
      `&redirect_uri=${encodeURIComponent(redirect_uri)}` +
      `&state=${state}` +
      `&scope=${encodeURIComponent(scope)}`;
    window.location.href = authorizationUrl;
  };

  return (
    <div className="dm-auth-page">
      <SeoMeta 
        title={isSignUp ? "Sign Up -- Ivori AI Learning Platform" : "Sign In -- Ivori AI Learning Platform"}
        description="Access your Ivori account to unlock admission test simulators, interactive case sandboxes, and personalized study analytics."
        keywords="ivori login, ivori sign up, ivori student portal, ivori account"
        url={`${EDU_CANONICAL_BASE}/login`}
      />

      {/* Section 1: Hero Section split into Left Form Pane and Right Cover Image */}
      <section className="workay-hero-section">
        
        {/* Left Pane: Editorial Warm Beige Form Pane */}
        <div className="workay-form-pane">
          <div className="workay-form-content">

            {/* Logo Row */}
            <div className="workay-brand-header">
              <a href={withAppBasename('/')} onClick={(e) => { e.preventDefault(); window.location.href = withAppBasename('/'); }} className="workay-logo-link">
                {isPathbuddyHost() ? (
                  <>
                    <img src="/logo-yellow.png" alt="Pathbuddy Logo" className="workay-logo-img" />
                    <span className="workay-brand-title">Pathbuddy</span>
                  </>
                ) : (
                  <span className="workay-brand-ivori">IVORI</span>
                )}
              </a>
            </div>

            {/* Headline Group with semantic H1 */}
            <div className="workay-headline-group">
              <h1 className="workay-title">
                {isSignUp
                  ? (isPathbuddyHost() ? 'Create your account.' : 'Create your account.')
                  : (isPathbuddyHost() ? 'Sign in to Pathbuddy.' : 'Sign in to IVORI.')}
              </h1>
              <p className="workay-subtitle">
                {isSignUp
                  ? (isPathbuddyHost() ? 'Sign up to start learning with Pathbuddy.' : 'Sign up to access your decision and admissions workspace.')
                  : (isPathbuddyHost() ? 'Access your admissions test preparation and decision workspace.' : 'Access your admissions test preparation and decision workspace.')}
              </p>
            </div>

            {/* Side-by-Side SSO Pill Buttons */}
            <div className="workay-sso-group">
              <button type="button" onClick={handleGoogleSSO} className="workay-sso-btn">
                <GoogleIcon />
                <span>Continue with Google</span>
              </button>

              <button type="button" onClick={handleLinkedInSSO} className="workay-sso-btn">
                <LinkedInIcon />
                <span>Continue with LinkedIn</span>
              </button>
            </div>

            {/* Or Divider */}
            <div className="workay-divider">
              <span className="workay-divider-line"></span>
              <span className="workay-divider-text">
                OR
              </span>
              <span className="workay-divider-line"></span>
            </div>

            {/* Alert Messages */}
            {errors.submit && (
              <div className="workay-alert error">
                <span>{errors.submit}</span>
              </div>
            )}
            {errors.success && (
              <div className="workay-alert success">
                <span>{errors.success}</span>
              </div>
            )}

            {/* Form */}
            <form className="workay-form" onSubmit={handleSubmit} noValidate>

              {/* Full Name (Sign Up Only) */}
              {isSignUp && (
                <div className="workay-field">
                  <label className="workay-label">Full Name</label>
                  <input
                    type="text"
                    name="name"
                    placeholder="Enter your full name"
                    value={formData.name || ''}
                    onChange={handleInputChange}
                    disabled={isLoading}
                    className="workay-input"
                  />
                  {errors.name && <span className="workay-field-error">{errors.name}</span>}
                </div>
              )}

              {/* Email */}
              <div className="workay-field">
                <label className="workay-label">E-mail address</label>
                <input
                  type="email"
                  name="email"
                  placeholder="name@example.com"
                  value={formData.email || ''}
                  onChange={handleInputChange}
                  disabled={isLoading}
                  className="workay-input"
                />
                {errors.email && <span className="workay-field-error">{errors.email}</span>}
              </div>

              {/* Password */}
              {(isSignUp || (!isSignUp && loginWithPassword)) && (
                <div className="workay-field">
                  <label className="workay-label">Password</label>
                  <div className="workay-input-rel">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      name="password"
                      placeholder={isSignUp ? 'Create a password' : '••••••••••••'}
                      value={formData.password || ''}
                      onChange={handleInputChange}
                      disabled={isLoading}
                      className="workay-input pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="workay-eye-btn"
                      aria-label={showPassword ? "Hide password" : "Show password"}
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                        {showPassword ? (
                          <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24M1 1l22 22" />
                        ) : (
                          <>
                            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                            <circle cx="12" cy="12" r="3" />
                          </>
                        )}
                      </svg>
                    </button>
                  </div>
                  {errors.password && <span className="workay-field-error">{errors.password}</span>}
                </div>
              )}

              {/* Checkbox Options */}
              <div className="workay-options-row">
                {isSignUp ? (
                  <label className="workay-checkbox-label">
                    <input
                      type="checkbox"
                      checked={agreeTerms}
                      onChange={(e) => setAgreeTerms(e.target.checked)}
                      className="workay-checkbox"
                    />
                    <span>I agree to the <a href={withAppBasename('/terms-of-use')} target="_blank" rel="noopener noreferrer">Terms</a> & <a href={withAppBasename('/privacy')} target="_blank" rel="noopener noreferrer">Privacy Policy</a></span>
                  </label>
                ) : (
                  <>
                    <label className="workay-checkbox-label">
                      <input type="checkbox" defaultChecked className="workay-checkbox" />
                      <span>Remember password</span>
                    </label>
                    {loginWithPassword && (
                      <a href={withAppBasename('/reset-password')} onClick={(e) => { e.preventDefault(); window.location.href = withAppBasename('/reset-password'); }} className="workay-link-underline">
                        Remind password
                      </a>
                    )}
                  </>
                )}
              </div>
              {errors.agreeTerms && <span className="workay-field-error">{errors.agreeTerms}</span>}

              {/* Sign In Buttons / Submit Button */}
              {!isSignUp ? (
                <>
                  {/* Continue with password */}
                  {!loginWithPassword && (
                    <button
                      type="button"
                      disabled={isLoading}
                      onClick={() => {
                        setLoginWithPassword(true);
                        setErrors({});
                      }}
                      className="workay-submit-btn"
                    >
                      Continue with password
                    </button>
                  )}

                  {/* Send magic link */}
                  {!loginWithPassword && (
                    <button
                      type="submit"
                      disabled={isLoading}
                      className="workay-submit-btn"
                    >
                      {isLoading ? 'Sending link...' : 'Send magic link'}
                    </button>
                  )}

                  {/* Password login */}
                  {loginWithPassword && (
                    <button
                      type="submit"
                      disabled={isLoading}
                      className="workay-submit-btn"
                    >
                      {isLoading ? 'Signing in...' : 'Sign in'}
                    </button>
                  )}
                </>
              ) : (
                /* Sign Up */
                <button
                  type="submit"
                  disabled={isLoading}
                  className="workay-submit-btn"
                >
                  {isLoading ? 'Creating account...' : 'Create account'}
                </button>
              )}
            </form>

            {/* Bottom Toggle Text */}
            <div className="workay-footer-toggle">
              {isSignUp ? (
                <>Already have an account? <button type="button" onClick={() => setIsSignUp(false)} className="workay-link-underline">Sign in</button></>
              ) : (
                <>Don't have an account? <button type="button" onClick={() => setIsSignUp(true)} className="workay-link-underline">Sign up</button></>
              )}
            </div>

          </div>
        </div>

      </section>

      {/* Section 2: Global 7-Column Footer Section */}
      <section className="dm-footer-section">
        <GlobalFooter />
      </section>
    </div>
  );
};

export default Login;